import { Leaf, Recycle, Wallet } from 'lucide-react';
import { useReductionLevers } from '@/api/hooks/useEmissions';
import { KpiCard } from '@/components/fleetguardian/KpiCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const ZAR = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  maximumFractionDigits: 0,
});
const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });
const DEC = new Intl.NumberFormat('en-ZA', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Explanatory copy sits beside the numbers — a lever nobody understands is a lever nobody pulls. */
function Note({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="px-6 pb-3 text-sm leading-relaxed text-muted-foreground">{children}</p>
  );
}

/**
 * Emissions reduction levers.
 *
 * Two actions the fleet can actually take, each quantified from its own data:
 * replace the vehicles burning more than their peers, and rotate the vehicles
 * whose workload is wildly uneven.
 *
 * Every comparison is PEER-RELATIVE — a vehicle against the median of its own
 * make+model, never against the fleet average. The fleet's thirstiest vehicles
 * in absolute terms are HINO, UD, IVECO and TATA trucks at 27-41 L/100km,
 * which is simply what a truck consumes; ranking on absolute efficiency would
 * recommend scrapping the truck fleet. A Hino only counts as a problem when it
 * is thirsty for a Hino.
 */
export function ReductionLevers(): JSX.Element {
  const { data, isLoading, error } = useReductionLevers();

  return (
    <>
      <div>
        <h2 className="text-lg font-semibold tracking-tight">How we cut this</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Two levers, each measured against what comparable vehicles in this
          fleet actually achieve — not a manufacturer figure and not a fleet-wide
          average.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Vehicles measured"
          value={data ? NUM.format(data.vehicles_scored) : '—'}
          subtitle="12 months of fills with odometer readings"
          icon={Leaf}
          hue="teal"
        />
        <KpiCard
          label="Fleet median"
          value={data ? `${DEC.format(data.fleet_median_l_per_100km)} L/100km` : '—'}
          subtitle="context only — vehicles are judged against their own model"
          icon={Recycle}
          hue="blue"
        />
        <KpiCard
          label="CO₂ recoverable"
          value={data ? `${NUM.format(data.renewal_tonnes_co2_saving)} t` : '—'}
          subtitle="per year, if each matched its own peers"
          icon={Leaf}
          hue="emerald"
        />
        <KpiCard
          label="Fuel cost recoverable"
          value={data ? ZAR.format(data.renewal_rand_saving) : '—'}
          subtitle="per year, at each vehicle's own R/L"
          icon={Wallet}
          hue="amber"
        />
      </div>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm">
            Lever 1 — Replace the vehicles burning more than their own model does
          </CardTitle>
        </CardHeader>
        <Note>
          {data ? (
            <>
              <strong className="text-foreground">
                {NUM.format(data.renewal_candidate_count)} vehicles
              </strong>{' '}
              are consuming more than <strong className="text-foreground">25%</strong>{' '}
              above the median for their <em>own make and model</em>. Bringing
              just those vehicles to what their identical siblings already
              achieve would avoid{' '}
              <strong className="text-foreground">
                {NUM.format(data.renewal_tonnes_co2_saving)} tonnes of CO₂
              </strong>{' '}
              and{' '}
              <strong className="text-foreground">
                {ZAR.format(data.renewal_rand_saving)}
              </strong>{' '}
              of fuel every year — without changing a single route or driver.
              Because the comparison is model-for-model, a thirsty truck is
              measured against other trucks, so this list is a genuine
              replacement queue rather than a list of big vehicles. Work it
              top-down: the vehicles at the top recover the most per rand of
              replacement capital.
            </>
          ) : (
            'Vehicles consuming materially more than the median for their own make and model.'
          )}
        </Note>
        <CardContent className="overflow-x-auto px-0">
          {error ? (
            <div className="px-6 py-3 text-sm text-destructive">
              Could not load reduction levers: {(error as Error).message}
            </div>
          ) : isLoading ? (
            <div className="space-y-2 px-6 pb-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Registration</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead className="text-right">Year</TableHead>
                  <TableHead className="text-right">L/100km</TableHead>
                  <TableHead className="text-right">Same model</TableHead>
                  <TableHead className="text-right">Excess</TableHead>
                  <TableHead className="text-right">tCO₂ / yr</TableHead>
                  <TableHead className="text-right">R / yr</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.renewal_candidates ?? []).map((c) => (
                  <TableRow key={c.asset_token}>
                    <TableCell className="font-mono text-xs">
                      {c.registration ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {[c.make, c.model].filter(Boolean).join(' ') || '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {c.manufacture_year ?? '—'}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {DEC.format(c.l_per_100km)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {DEC.format(c.peer_l_per_100km)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums text-destructive">
                      +{NUM.format(c.excess_pct)}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {DEC.format(c.tonnes_co2_saving)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {ZAR.format(c.rand_saving)}
                    </TableCell>
                  </TableRow>
                ))}
                {!isLoading && !(data?.renewal_candidates ?? []).length && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      No vehicle is materially worse than its own model peers.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm">
            Lever 2 — Rotate the workload inside a cost centre
          </CardTitle>
        </CardHeader>
        <Note>
          Within a single cost centre one vehicle can cover a few thousand
          kilometres in a year while a sibling covers well over a hundred
          thousand. Swapping them levels wear, delays the replacement of the
          hard-worked vehicle, and moves distance onto the unit that is barely
          being used — which is free, unlike buying anything. A vehicle sitting
          nearly idle is also the cheapest one to redeploy or hand back, so this
          list doubles as a downsizing shortlist. Each row pairs the idlest
          vehicle in that cost centre with the busiest.
        </Note>
        <CardContent className="overflow-x-auto px-0">
          {isLoading ? (
            <div className="space-y-2 px-6 pb-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cost centre</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead className="text-right">Vehicles</TableHead>
                  <TableHead>Under-used</TableHead>
                  <TableHead className="text-right">km / yr</TableHead>
                  <TableHead>Over-used</TableHead>
                  <TableHead className="text-right">km / yr</TableHead>
                  <TableHead className="text-right">Imbalance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.rotation_candidates ?? []).map((c) => (
                  <TableRow key={c.cost_centre_code}>
                    <TableCell className="font-mono text-xs">
                      {c.cost_centre_code}
                    </TableCell>
                    <TableCell className="text-sm">{c.district_desc ?? '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.vehicles}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {c.low_registration ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {NUM.format(c.low_km_12m)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {c.high_registration ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {NUM.format(c.high_km_12m)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums text-destructive">
                      {NUM.format(c.utilisation_ratio)}×
                    </TableCell>
                  </TableRow>
                ))}
                {!isLoading && !(data?.rotation_candidates ?? []).length && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="py-8 text-center text-sm text-muted-foreground"
                    >
                      No cost centre shows a material utilisation imbalance.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm">How these numbers are derived</CardTitle>
        </CardHeader>
        <Note>
          CO₂ is computed from fuel volume at 2,55 kg per litre, the
          fleet-blended factor (diesel ~2,68 · petrol ~2,31); the transaction
          feed does not yet carry fuel grade, so a per-grade split is not
          possible. Distance is measured from odometer readings, which are
          present on 99,9% of the last year&apos;s fills — not estimated.
          Vehicles need at least six fills and 1 000 km before they are scored,
          and each model needs at least eight examples before it can define its
          own median, so a two-vehicle model cannot set its own standard. Bulk
          fuel cards are excluded: they are department cards, not vehicles, and
          their odometer readings come from many different vehicles. Spans above
          150 000 km/year are excluded as odometer resets rather than travel.
        </Note>
      </Card>
    </>
  );
}

export default ReductionLevers;
